/**
 * StuckJobsTable — presentational table for the Stuck jobs page (DEC-315 options A and B).
 *
 * Every timestamp goes through formatCreated with the dispatcher's profile timezone (DEC-004 /
 * DEC-066: local, 12-hour, never UTC). The age line is formatAge against `nowMs`, which the
 * page refreshes with each data update so the age does not drift from the list.
 *
 * The Close clock button is shown only when the caller's role can force-close
 * (`canForceClose`) AND the row's clock is one dispatcher-close would accept (`closable`).
 * A row whose clock a dispatcher already stopped stays listed, labelled "Clock stopped — job
 * still open", with no button: the job's status is untouched by a close.
 *
 * DEC-315 option B: a row with a captured load shows "Load captured — needs completion" to
 * every viewer and never a Cancel job button (the API refuses it). Otherwise the Cancel job
 * button is shown when the caller's role is in the stuck-cancel set (`canStuckCancel`). The
 * Close clock rule is unchanged; the action cell stacks whatever applies.
 *
 * DEC-321 / OPEN-082: under that label, a "Send to review" button — the way such a job gets
 * finished. DEC-329: who sees it, and what it says, follow the company policy
 * (`forceReviewMode`): "Send to review" when the click forces, "Ask a manager to send to
 * review" when it files a request, nothing under NEVER. A row whose request is already waiting
 * says so instead of offering a second request.
 *
 * #42: clicking a row calls `onRowClick`, which opens the shared JobDetailModal: created
 * by/when, notes, audit timeline, GPS trail. It is how a dispatcher sees who made a job that
 * has no ticket id and no site. Clicks and keys inside the action cell stop there, so Close
 * clock / Cancel job / Send to review / Void load / Ask driver to sync never also open it.
 * DEC-355 (OPEN-151 PR 2): the same row also offers "Void load and cancel" to the stuck-cancel
 * roles, whatever the policy (it does not apply to a void), for a load that should not exist.
 *
 * #54: every row action is a small icon button in one line (no overflow menu), each with a
 * tooltip and an aria-label naming the action; Cancel job and Void load are red. The row's own
 * click replaced the "Details" button. The notes (captured load, waiting for approval, the
 * sync-nudge status) print under the icons.
 */

import * as React from 'react';
import { Ban, CircleX, ClipboardCheck, TimerOff, type LucideIcon } from 'lucide-react';
import { Button } from 'src/components/ui/button';
import { formatCreated } from '../../lib/timestamp';
import { formatAge, syncNudgeStatusText } from './stuck-jobs-format';
import type { StuckJobRow } from './use-stuck-jobs';
import { SyncNudgeButton } from './SyncNudgeButton';
import type { ForceReviewMode } from './completion-policy';

export interface StuckJobsTableProps {
  readonly rows: readonly StuckJobRow[];
  readonly timeZone: string;
  readonly canForceClose: boolean;
  readonly nowMs: number;
  readonly onCloseClick: (row: StuckJobRow) => void;
  readonly canStuckCancel: boolean;
  readonly onCancelClick: (row: StuckJobRow) => void;
  /** DEC-321 — "Send to review" on a captured-load row. */
  readonly onForceReviewClick: (row: StuckJobRow) => void;
  /** DEC-355 — "Void load and cancel" on a captured-load row, for `canStuckCancel` roles. */
  readonly onVoidLoadClick?: (row: StuckJobRow) => void;
  /** DEC-328 — show "Ask driver to sync" (SYNC_NUDGE_ROLES). */
  readonly canSyncNudge: boolean;
  /** DEC-329 — what this viewer's click does under the company policy (completion-policy.ts). */
  readonly forceReviewMode: ForceReviewMode;
  /** DEC-329 — jobs with a request already waiting for a manager. */
  readonly pendingJobIds: ReadonlySet<string>;
  /** #42 — open the job detail modal for this row. */
  readonly onRowClick: (row: StuckJobRow) => void;
}

const thStyle: React.CSSProperties = {
  textAlign: 'left',
  padding: '8px 10px',
  fontSize: '11px',
  fontWeight: 700,
  color: 'var(--color-text-tertiary)',
  borderBottom: '1px solid var(--color-border-secondary)',
  whiteSpace: 'nowrap',
};

const tdStyle: React.CSSProperties = {
  padding: '8px 10px',
  fontSize: '13px',
  color: 'var(--color-text-primary)',
  borderBottom: '1px solid var(--color-border-secondary)',
  verticalAlign: 'top',
};

const mutedStyle: React.CSSProperties = {
  fontSize: '12px',
  color: 'var(--color-text-tertiary)',
};

const badgeStyle: React.CSSProperties = {
  display: 'inline-block',
  padding: '2px 8px',
  borderRadius: '10px',
  fontSize: '12px',
  fontWeight: 600,
  whiteSpace: 'nowrap',
  border: '1px solid var(--color-border-secondary)',
  backgroundColor: 'var(--color-bg-tertiary)',
  color: 'var(--color-text-secondary)',
};

const amberStyle: React.CSSProperties = {
  color: 'var(--color-warning)',
  fontWeight: 600,
};

const COLUMNS: readonly string[] = [
  'Ticket',
  'Status',
  'Driver',
  'Customer / Site',
  'Last activity',
  'Why',
  'Clock',
  'Action',
];

const CLOCK_HEAD_LABEL: Readonly<Record<NonNullable<StuckJobRow['clockHead']>, string>> = {
  STARTED: 'Running',
  RESUMED: 'Running (resumed)',
  PAUSED: 'Paused',
  STOPPED: 'Stopped',
};

function reasonLabel(row: StuckJobRow): string {
  const h = row.thresholdHours.toString();
  return row.reason === 'PAUSED_STALE' ? `Paused > ${h}h` : `No activity > ${h}h`;
}

const DANGER = 'var(--color-danger)';

/** #54 — a row action as a small icon button; the label is both tooltip and aria-label. */
function RowIconButton({
  label,
  icon: Icon,
  danger = false,
  onClick,
}: {
  readonly label: string;
  readonly icon: LucideIcon;
  readonly danger?: boolean;
  readonly onClick: () => void;
}): React.JSX.Element {
  return (
    <Button
      type="button"
      variant="outline"
      size="icon"
      className="h-8 w-8"
      title={label}
      aria-label={label}
      style={danger ? { color: DANGER, borderColor: DANGER } : undefined}
      onClick={onClick}
    >
      <Icon aria-hidden="true" />
    </Button>
  );
}

/** DEC-328 — the line SyncNudgeButton prints in its full form, here under the icons. */
function SyncNudgeStatus({
  row,
  nowMs,
}: {
  readonly row: StuckJobRow;
  readonly nowMs: number;
}): React.JSX.Element | null {
  const status = syncNudgeStatusText(row.lastNudgeAt, row.lastNudgeCheckinAt, nowMs);
  if (status === null) return null;
  return (
    <div style={mutedStyle} data-testid={`sync-nudge-status-${row.jobId}`}>
      {status}
    </div>
  );
}

function ClockCell({ row }: { readonly row: StuckJobRow }): React.JSX.Element {
  if (row.clockStopped) {
    return <span style={amberStyle}>Clock stopped — job still open</span>;
  }
  if (row.clockHead === null) {
    return <span style={mutedStyle}>No clock</span>;
  }
  return <span>{CLOCK_HEAD_LABEL[row.clockHead]}</span>;
}

export function StuckJobsTable({
  rows,
  timeZone,
  canForceClose,
  nowMs,
  onCloseClick,
  canStuckCancel,
  onCancelClick,
  onForceReviewClick,
  onVoidLoadClick,
  canSyncNudge,
  forceReviewMode,
  pendingJobIds,
  onRowClick,
}: StuckJobsTableProps): React.JSX.Element {
  const stop = (e: React.SyntheticEvent): void => e.stopPropagation();
  return (
    <table style={{ width: '100%', borderCollapse: 'collapse' }}>
      <thead>
        <tr>
          {COLUMNS.map((c) => (
            <th key={c} style={thStyle}>
              {c}
            </th>
          ))}
        </tr>
      </thead>
      <tbody>
        {rows.length === 0 ? (
          <tr>
            <td
              colSpan={COLUMNS.length}
              style={{ ...tdStyle, color: 'var(--color-text-tertiary)' }}
            >
              No stuck jobs
            </td>
          </tr>
        ) : (
          rows.map((row) => (
            <tr
              key={row.jobId}
              data-testid={`stuck-row-${row.jobId}`}
              onClick={(): void => onRowClick(row)}
              style={{ cursor: 'pointer' }}
            >
              <td style={tdStyle}>{row.haulId ?? '—'}</td>
              <td style={tdStyle}>{row.statusLabel}</td>
              <td style={tdStyle}>{row.driverName ?? '—'}</td>
              <td style={tdStyle}>
                <div>{row.customerName ?? '—'}</div>
                <div style={mutedStyle}>{row.siteName ?? '—'}</div>
              </td>
              <td style={tdStyle} data-testid={`stuck-last-activity-${row.jobId}`}>
                <div style={{ whiteSpace: 'nowrap' }}>
                  {formatCreated(row.lastActivityAt, timeZone)}
                </div>
                <div style={mutedStyle}>{formatAge(row.lastActivityAt, nowMs)}</div>
              </td>
              <td style={tdStyle}>
                <span style={badgeStyle}>{reasonLabel(row)}</span>
              </td>
              <td style={tdStyle}>
                <ClockCell row={row} />
                {row.lastActivitySource === 'JOB_UPDATED_AT' && (
                  <div style={mutedStyle}>(no field events — last edit time)</div>
                )}
              </td>
              <td
                style={tdStyle}
                onClick={stop}
                onKeyDown={stop}
                data-testid={`stuck-actions-${row.jobId}`}
              >
                <div style={{ display: 'flex', flexWrap: 'wrap', gap: '6px' }}>
                  {canSyncNudge && row.driverId !== null ? (
                    <SyncNudgeButton
                      jobId={row.jobId}
                      lastNudgeAt={row.lastNudgeAt}
                      lastNudgeCheckinAt={row.lastNudgeCheckinAt}
                      nowMs={nowMs}
                      iconOnly
                    />
                  ) : null}
                  {canForceClose && row.closable && !row.clockStopped ? (
                    <RowIconButton
                      label="Close clock"
                      icon={TimerOff}
                      onClick={(): void => onCloseClick(row)}
                    />
                  ) : null}
                  {row.hasCapturedLoad ? (
                    <>
                      {!pendingJobIds.has(row.jobId) && forceReviewMode !== 'NONE' ? (
                        <RowIconButton
                          label={
                            forceReviewMode === 'REQUEST'
                              ? 'Ask a manager to send to review'
                              : 'Send to review'
                          }
                          icon={ClipboardCheck}
                          onClick={(): void => onForceReviewClick(row)}
                        />
                      ) : null}
                      {canStuckCancel && onVoidLoadClick !== undefined ? (
                        <RowIconButton
                          label="Void load and cancel"
                          icon={Ban}
                          danger
                          onClick={(): void => onVoidLoadClick(row)}
                        />
                      ) : null}
                    </>
                  ) : canStuckCancel ? (
                    <RowIconButton
                      label="Cancel job"
                      icon={CircleX}
                      danger
                      onClick={(): void => onCancelClick(row)}
                    />
                  ) : null}
                </div>
                {row.hasCapturedLoad ? (
                  <div style={amberStyle}>Load captured — needs completion</div>
                ) : null}
                {row.hasCapturedLoad && pendingJobIds.has(row.jobId) ? (
                  <div style={mutedStyle} data-testid={`stuck-pending-${row.jobId}`}>
                    Waiting for manager approval
                  </div>
                ) : null}
                {canSyncNudge && row.driverId !== null ? (
                  <SyncNudgeStatus row={row} nowMs={nowMs} />
                ) : null}
              </td>
            </tr>
          ))
        )}
      </tbody>
    </table>
  );
}
